// React hook imports
import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Custom hooks imports
import { useNavigateWithSameKey } from "../../../../../../../../../../../custom-hooks/navigateWithKey";

// Redux imports
import { useDispatch, useSelector } from "react-redux";
import { setLocatorsState, setPageDOMState } from "../../../../../../../../../../../store/slices/test-automation/web/flow-stages/playwirghtCSharpReqnrollSlice";

// Context imports
import {
  LoadingModalContext,
  MessagePopupContext,
} from "../../../../../../../../../ServicePage";

// MUI components and icon imports
import { Box, Button } from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
} from "@mui/icons-material";

// Project components imports
import IntroduceSUTPage from "../../../../../shared/IntroduceSUTPage";

// Custom components import
import { handleProgressBar } from "../../../../../../../../../../custom-components/ProgressBar/ProgressBarWithAPI";

// Project utilities
import { pageSourceService } from "../../../../../../../../../../../services/webServices";

const ReqNRollDOMPage = () => {
  const navigate = useNavigateWithSameKey();
  const location = useLocation();
  const dispatch = useDispatch();

  // Capture locationKey from state
  const { locationKey } = location.state;

  // Capture data from redux store using selector
  const { pageDOM, automationPath } = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "playwrightCSharpReqNRoll"
      ] || {}
  );

  // Capture set methods from context
  const { setLoadingState } = useContext(LoadingModalContext);
  const { setMessage, setMessagePopup } = useContext(MessagePopupContext);

  // Function to be called when locationKey updated
  useEffect(() => {
    if (!locationKey) {
      navigate("/landingpage");
      return;
    }
  }, [locationKey]);

  const handleBackClick = () => {
    // Navigate to the previous step (Feature file generated)
    navigate(-1);
  };

  const handleContinueClick = async () => {
    try {
      let progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 18000,
      });
      progressHandler.start("Generating locators...");
      const locatorsResponse = await pageSourceService({
        pageSource: pageDOM,
      });
      dispatch(setLocatorsState(locationKey, locatorsResponse.data));
      // Navigate to next step...
      navigate(`${automationPath}/locators`);
      progressHandler.stop();
    } catch (error) {
      setMessage(
        error?.response?.data?.message || "Couldn't process your request"
      );
      setMessagePopup(true);
    } finally {
      setLoadingState({
        open: false,
        message: "",
        progress: null,
      })
    }
  };

  const handleSaveContent = (content) => {
    dispatch(setPageDOMState(locationKey, content));
  };

  return (
    <Box sx={{ height: "100%" }}>
      <IntroduceSUTPage
        title="Introduce SUT"
        content={pageDOM ? pageDOM : "Enter Page Sorce here..."}
        handleSaveContent={handleSaveContent}
      />

      <Box
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "transparent",
          borderRadius: "0 0 8px 8px",
        }}
      >
        {/* Back Button */}
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBackClick}
          sx={{
            height: "40px",
            borderColor: "#0094FB",
            color: "#0094FB",
            textTransform: "none",
            // fontFamily: "Manrope",
            fontWeight: 500,
            borderRadius: "8px",
            borderWidth: "2px",
            paddingTop: "8px",
            paddingRight: "16px",
            paddingBottom: "8px",
            paddingLeft: "16px",
            gap: "8px",
            "&:hover": {
              borderColor: "#0094FB",
              backgroundColor: "rgba(0, 148, 251, 0.04)",
            },
          }}
        >
          Back
        </Button>

        {/* Continue Button */}
        <Button
          variant="contained"
          endIcon={<NextIcon />}
          onClick={handleContinueClick}
          disabled={!pageDOM || pageDOM === ""}
          sx={{
            height: "40px",
            backgroundColor: "#0094FB",
            color: "white",
            textTransform: "none",
            // fontFamily: "Manrope",
            fontWeight: 500,
            borderRadius: "8px",
            borderWidth: "2px",
            borderColor: "#0094FB",
            paddingTop: "8px",
            paddingRight: "16px",
            paddingBottom: "8px",
            paddingLeft: "16px",
            "&:hover": {
              backgroundColor: "#0078D4",
              borderColor: "#0078D4",
            },
          }}
        >
          Generate Locators
        </Button>
      </Box>
    </Box>
  );
};

export default ReqNRollDOMPage;
